import React from 'react';
import { BrowserRouter, Navigate, Route, Routes, Link, useNavigate, useLocation } from 'react-router-dom';

// OBSOLETE import AuthRoutes from './routes/AuthRoutes';
// OBSOLETE import AppRoutes from './routes/AppRoutes'
import IndexRoute from './routes';

import "bootstrap/js/src/collapse";
import ScrollToTop from './utils/ScrollToTop';
import { MsalProvider, AuthenticatedTemplate, useMsal, UnauthenticatedTemplate } from '@azure/msal-react';
import {PageLayout} from './components/ms-entra/PageLayout'
import { Container, Button, Card, Row, Col } from 'react-bootstrap';
import Signup from './views/Authentication/SignUp/Signup';
// import Signup from './components/telescope/TEMPORARYSignup';
import { useEffect, useState } from 'react';
import { loginRequest } from './authConfig';
import { useEntraLoginRedirect, startEntraLoginRedirect } from './utils/useEntraLoginRedirect';
import { APP_VERSION } from './constants';

// TanStack Query
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { queryClient } from './utils/queryClient';

//Images
import logo from './assets/img/logo-dark.png';
import logoBlank from './assets/img/logo-blank.png'; // white, the old BioMADE logo's size, so the Telescope logo stays put
import signupBg from './assets/img/signup-bg.jpg';
import slide1 from './assets/img/slide1.jpg';
import slide2 from './assets/img/slide2.jpg';
import AuthenticatedLandingPage from "./views/AuthenticatedLandingPage";
import NotImplementedYet from './views/NotImplementedYet';
import TestView from './views/TestView';
import Health from './views/health';
import Error404 from './views/Authentication/Error404/Error404';
import Error503 from "./views/Authentication/Error503/Error503";
import ResetPassword from './views/Authentication/ResetPassword';
import AuthorizationServiceUnavailable from './views/Authentication/AuthorizationServiceUnavailable';
import Signout from './views/signout';
import { setNavigate } from "./utils/navigation";

import { Exceptionless, ExceptionlessErrorBoundary } from "@exceptionless/react";
import { logException, logInfo } from './services/LoggingService';
import { getUserName, isTenantSelectionPending } from './utils/tokenUtils';
import SelectTenantPage from './views/Auth/SelectTenantPage';

// Holds at /redirect with a spinner until main.jsx signals all async login work
// is done, then routes to /select-tenant (multi-tenant) or /landing (single-tenant).
function PostLoginHold() {
    const nav = useNavigate();
    useEffect(() => {
        const onReady = () => {
            nav(isTenantSelectionPending() ? '/select-tenant' : '/landing', { replace: true });
        };
        window.addEventListener('telescopePostLoginReady', onReady);
        // Never enter the application shell without a completed BADDOG session.
        const timeout = setTimeout(() => nav('/authorization-service-unavailable', { replace: true }), 30000);
        return () => {
            window.removeEventListener('telescopePostLoginReady', onReady);
            clearTimeout(timeout);
        };
    }, [nav]);

    return (
        <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
            <div className="text-center">
                <div className="spinner-border" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2">Setting up your session...</p>
            </div>
        </div>
    );
}

// DEV/TEST
// console.log("VITE_APP_VERSION: " + import.meta.env.VITE_APP_VERSION )
// console.log("VITE_APP_API_HOST_URL: " + import.meta.env.VITE_APP_API_HOST_URL )
// console.log("VITE_APP_API_VERSION: " + import.meta.env.VITE_APP_API_VERSION )

/**
 * Most applications will need to conditionally render certain components based on whether a user is signed in or not.
 * msal-react provides 2 easy ways to do this. AuthenticatedTemplate and UnauthenticatedTemplate components will
 * only render their children if a user is authenticated or unauthenticated, respectively. For more, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-react/docs/getting-started.md
 */
const MainContent = () => {

  console.log("=== MainContent Rendered ===");
  
  // Build date and version info
  const buildDate = __BUILD_DATE__;
  const buildDateLocal = new Date(buildDate).toLocaleString();
  
  /**
   * useMsal is hook that returns the PublicClientApplication instance,
   * that tells you what msal is currently doing. For more, visit:
   * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-react/docs/hooks.md
   */
  const { instance, accounts, inProgress } = useMsal();
  const { loginRedirect: entraLoginRedirect, isLoginPending } = useEntraLoginRedirect();
  const activeAccount = instance.getActiveAccount();

  const [token, setToken] = useState(null);

  console.log("=== MainContent Debug Info ===");
  console.log("Active Account:", activeAccount);
  console.log("Accounts:", accounts);
  console.log("InProgress:", inProgress);
  console.log("=== End Debug Info ===");

  useEffect(() => {

    const initializeExceptionless = async () => {
      await Exceptionless.startup((c) => {

        c.apiKey = "XpAiE4LgEftsm9zw6Q5yTcmfT5DBOzsK2FATPuA4";

        const userName = getUserName() ? getUserName() : "Unauthenticated User";
        const name = activeAccount ? activeAccount.name : "Unauthenticated User";
        c.setUserIdentity(userName, name);

      });
    };
    initializeExceptionless();

  }, []);
  
  useEffect(() => {
    
      const activeAccount = instance.getActiveAccount();
      if (activeAccount) {
          instance.acquireTokenSilent({
              account: activeAccount,
              scopes: ["openid", "profile", "email"]
          })
          .then(response => {

            // console.log("New Token:", response.idToken);
            console.log("==================================================================");
            console.log("acquireTokenSilent()");
            console.log("Active Account:", activeAccount);
            console.log("`app.jsx` ID Token:", activeAccount.idToken);
            console.log("==================================================================");

            setToken(response.idToken); // Store token in state
            activeAccount.idToken = response.idToken;
            activeAccount.idTokenClaims = response.idTokenClaims;

            const userName = getUserName() ? getUserName() : "Unauthenticated User";
            const name = activeAccount ? activeAccount.name : "Unauthenticated User";
            
            Exceptionless.config.setUserIdentity(userName, name);
            logInfo(`AcquireTokenSilent for user: ${userName} (Token acquired silently)`, 'MS-ENTRA Authentication');

          })
          .catch(error => {
            console.error("Silent token acquisition failed", error);
            logException(error);
          });
      }
  }, [instance]); // Runs after the first render and when `instance` changes

  const handleRedirect = () => {
    startEntraLoginRedirect(instance, {
        ...loginRequest,
        prompt: 'create',
     }).catch((error) => {
        console.log(error)
        logException(error);
      });
  };

  const handleLoginRedirect = () => {
    entraLoginRedirect().catch((error) => {
      console.log(error)
      logException(error);
    });
  };

  function LoginCard() {
    const showLoginPending = isLoginPending || inProgress !== 'none';
    return (
      
      <>
      <Container>
        <img className="brand-img img-fluid mt-10"  style={{ display: "block", maxWidth: "10%", height: "auto", margin: "0 auto"  }} src={logoBlank} alt="" />
        <img className="brand-img img-fluid"  style={{ display: "block", maxWidth: "35%", height: "auto", margin: "0 auto", marginTop: "-60px"  }} src={logo} alt="brand" />
      </Container>

      <Container>

        <Row>
          <Col xxl={5} xl={7} lg={10} className="mx-auto">
          {/* <Col md={6}>     */}
            <Card className="text-center">
              <Card.Body>
                <Card.Title>
                  <h3>Welcome to Telescope</h3>
                </Card.Title>
                {/* Show loading state during authentication */}
                {showLoginPending ? (
                  <div>
                    <div className="spinner-border" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-2">Signing you in...</p>
                  </div>
                ) : (
                  <>
                    &nbsp;
                    <Button
                      className="signInButton btn-uppercase"
                      onClick={handleLoginRedirect}
                      variant="secondary"
                      disabled={showLoginPending}
                      aria-disabled={showLoginPending}
                    >
                      Sign In
                    </Button>
                    <div className="mt-3">
                      <Link to="/reset-password" className="text-muted">
                        <small>Forgot your password?</small>
                      </Link>
                    </div>
                  </>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
        
        {/* Version information */}
        <Row className="mt-3">
          <Col className="text-center">
            <small className="text-muted">
              version: <strong>{APP_VERSION}</strong> &nbsp;--&nbsp;built: <strong>{buildDateLocal}</strong>
            </small>
          </Col>
        </Row>
      
      </Container>
      </>
    );
  }

  // TESTING NEW CODE
  const navigate = useNavigate();
  useEffect(() => {
    setNavigate(navigate);
  }, [navigate]);
  // TESTING NEW CODE

  
  return (

    <ExceptionlessErrorBoundary>

      <div className="App">

        <ScrollToTop>

          <Routes>

            <Route path="/redirect" element={<PostLoginHold />} />

            <Route path="/authorization-service-unavailable" element={
              <>
                <AuthenticatedTemplate>
                  <AuthorizationServiceUnavailable />
                </AuthenticatedTemplate>
                <UnauthenticatedTemplate>
                  <Navigate to="/login" replace />
                </UnauthenticatedTemplate>
              </>
            } />

            <Route path="/signout" element={
              <AuthenticatedTemplate>
                <Signout />
              </AuthenticatedTemplate>
            } />

            <Route path="/test/auto-signout" element={
              <AuthenticatedTemplate>
                <Signout />
              </AuthenticatedTemplate>
            } />

            {/* Public routes that don't require authentication */}
            <Route path="/testme" element={<NotImplementedYet message="TEST ME" />} />
            <Route path="/error-404" element={<Error404 />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            
            {/* Login route - only show if not authenticated */}
            <Route path="/login" element={
              <UnauthenticatedTemplate>
                <LoginCard />
              </UnauthenticatedTemplate>
            } />

            {/* Root path - redirect based on auth status */}
            <Route path="/" element={
              <>
                <AuthenticatedTemplate>
                  <Navigate to="/landing" replace />
                </AuthenticatedTemplate>
                <UnauthenticatedTemplate>
                  <LoginCard />
                </UnauthenticatedTemplate>
              </>
            } />

            {/* Landing route - needs to be outside of IndexRoute for immediate access after login */}
            <Route path="/landing" element={
              <>
                {inProgress === "none" ? (
                  <>
                    <AuthenticatedTemplate>
                      <IndexRoute />
                    </AuthenticatedTemplate>
                    <UnauthenticatedTemplate>
                      <Navigate to="/login" replace />
                    </UnauthenticatedTemplate>
                  </>
                ) : (
                  <div className="d-flex justify-content-center align-items-center" style={{ height: "100vh" }}>
                    <div className="text-center">
                      <div className="spinner-border" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <p className="mt-2">Processing authentication...</p>
                    </div>
                  </div>
                )}
              </>
            } />

            {/* Tenant selection - shown after login when user has multiple tenants */}
            <Route path="/select-tenant" element={
              <>
                {inProgress === "none" ? (
                  <>
                    <AuthenticatedTemplate>
                      <SelectTenantPage />
                    </AuthenticatedTemplate>
                    <UnauthenticatedTemplate>
                      <Navigate to="/login" replace />
                    </UnauthenticatedTemplate>
                  </>
                ) : (
                  <div className="d-flex justify-content-center align-items-center" style={{ height: "100vh" }}>
                    <div className="text-center">
                      <div className="spinner-border" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <p className="mt-2">Processing authentication...</p>
                    </div>
                  </div>
                )}
              </>
            } />

            {/* Unprotected routes - accessible without authentication */}
            <Route path="/health" element={<Health />} />
            
            {/* All other routes go through IndexRoute which has its own protection */}
            <Route path="/*" element={
              <>
                {inProgress === "none" ? (
                  <>
                    <AuthenticatedTemplate>
                      <IndexRoute />
                    </AuthenticatedTemplate>
                    <UnauthenticatedTemplate>
                      <Navigate to="/login" replace />
                    </UnauthenticatedTemplate>
                  </>
                ) : (
                  <div className="d-flex justify-content-center align-items-center" style={{ height: "100vh" }}>
                    <div className="text-center">
                      <div className="spinner-border" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <p className="mt-2">Processing authentication...</p>
                    </div>
                  </div>
                )}
              </>
            } />

          </Routes>

        </ScrollToTop>

      </div>
 
    </ExceptionlessErrorBoundary>

  );
};



/**
 * msal-react is built on the React context API and all parts of your app that require authentication must be
 * wrapped in the MsalProvider component. You will first need to initialize an instance of PublicClientApplication
 * then pass this to MsalProvider as a prop. All components underneath MsalProvider will have access to the
 * PublicClientApplication instance via context as well as all hooks and components provided by msal-react. For more, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-react/docs/getting-started.md
 */
const App = ({ instance }) => {

  return (
      <MsalProvider instance={instance}>
          <QueryClientProvider client={queryClient}>
              <PageLayout>
                  <BrowserRouter>
                      <MainContent />
                  </BrowserRouter>
              </PageLayout>
              {/* TanStack Query DevTools - only in development */}
              {import.meta.env.DEV && (
                  <ReactQueryDevtools initialIsOpen={false} />
              )}
          </QueryClientProvider>
      </MsalProvider>
  );
};

export default App;